import { useContext } from 'react';
import { AppContext, AjaxContext, formatListData } from 'commons-ui';
import _reduce from 'lodash/reduce';
import _keys from 'lodash/keys';
import _get from 'lodash/get';
import { ZOOKEEPER_ENDPOINTS } from './helper';

const useService = () => {
	const { postData, getData } = useContext(AjaxContext);

	const {
		app: { appName }
	} = useContext(AppContext);

	const createZkKey = async ({
		keyName,
		keyValue,
		game,
		serviceName,
		agent,
		desc
	}: {
		keyName: string;
		keyValue: object;
		game: string;
		serviceName: string;
		desc: string;
		agent: string;
	}) => {
		const res = await postData({
			endpoint: ZOOKEEPER_ENDPOINTS.UPDATE_KEY,
			queryParams: {
				serviceName,
				game,
				appName,
				key: keyName,
				approvalRequired: true
			},
			body: {
				data: keyValue,
				agent,
				desc
			},
			returnResponse: true
		});
		return res;
	};

	const getKeyValue = async ({ keyName, serviceName, game }: { keyName: string; serviceName: string; game: string }) => {
		const res = await getData({
			endpoint: ZOOKEEPER_ENDPOINTS.GET_KEY,
			queryParams: {
				serviceName,
				game,
				key: keyName,
				appName
			}
		});
		return res.data;
	};

	const getUpdateReqs = async ({ serviceName, game }: { serviceName: string; game: string }) => {
		const res = await getData({
			endpoint: ZOOKEEPER_ENDPOINTS.GET_ZK_UPDATED_REQS,
			queryParams: {
				serviceName,
				game,
				appName
			}
		});
		const updateReqData = res?.data?.data;
		const rowCount = _keys(updateReqData).length;
		const formattedData = formatListData(updateReqData);
		return { rowCount, data: formattedData };
	};

	const updateZKUpdateReq = async ({
		serviceName,
		game,
		reqId,
		action,
		otp
	}: {
		serviceName: string;
		reqId: string;
		action: string;
		game: string;
		otp?: number;
	}): Promise<{ success: string; isError?: boolean; message?: string }> => {
		const res = await postData({
			endpoint: ZOOKEEPER_ENDPOINTS.UPDATE_ZK_UPDATED_REQS,
			queryParams: {
				serviceName,
				game,
				appName
			},
			body: {
				action,
				reqId,
				otp
			},
			returnResponse: true
		});

		return res.data;
	};

	const sendZkUpdateReqOtp = async ({
		serviceName,
		game,
		reqId
	}: {
		serviceName: string;
		reqId: string;
		game: string;
	}): Promise<{ success: string; isError?: boolean; message?: string }> => {
		const res = await postData({
			endpoint: ZOOKEEPER_ENDPOINTS.GET_OTP,
			queryParams: {
				serviceName,
				game,
				appName
			},
			body: {
				id: reqId
			},
			returnResponse: true
		});

		return res.data;
	};

	const updateKeyValue = async ({
		keyName,
		serviceName,
		agent,
		desc,
		data,
		game
	}: {
		keyName: string;
		serviceName: string;
		agent: string;
		desc: string;
		data: string;
		game: string;
	}) => {
		const res = await postData({
			endpoint: ZOOKEEPER_ENDPOINTS.UPDATE_KEY,
			queryParams: {
				serviceName,
				game,
				key: keyName,
				appName,
				approvalRequired: true
			},
			body: {
				data,
				agent,
				desc
			},
			returnResponse: true
		});

		return res.data;
	};

	const fetchServiceKeys = async (serviceName: string, game: string) => {
		const res = (
			await getData({
				endpoint: ZOOKEEPER_ENDPOINTS.LIST_KEYS,
				queryParams: {
					serviceName,
					game,
					appName
				}
			})
		).data;

		const serviceKeys = _reduce(
			res.servicekeys,
			(collector: any, item: any, index: any) => {
				const newCollector = collector;
				newCollector[index] = {
					label: item,
					serviceName
				};
				return newCollector;
			},
			[]
		);
		const commonKeys = _reduce(
			res.commonsKey,
			(collector: any, item: any, index: any) => {
				const newCollector = collector;
				newCollector[index] = {
					label: item,
					serviceName: 'commons'
				};
				return newCollector;
			},
			[]
		);

		const totalKeys = [...serviceKeys, ...commonKeys];
		const rowCount = totalKeys.length;

		return {
			rowCount,
			data: formatListData(totalKeys, 'label', (data) => data)
		};
	};

	const getZkServices = async () => {
		const res = await getData({
			endpoint: ZOOKEEPER_ENDPOINTS.LIST_SERVICES,
			queryParams: {
				appName
			}
		});
		const servicesData = _get(res, ['data', 'services'], {});
		const rowCount = _keys(servicesData).length;
		const formattedData = formatListData(servicesData, 'label', (data) => ({ label: data }));
		return { rowCount, data: formattedData };
	};

	return {
		createZkKey,
		getKeyValue,
		updateKeyValue,
		fetchServiceKeys,
		getZkServices,
		getUpdateReqs,
		updateZKUpdateReq,
		sendZkUpdateReqOtp
	};
};

export default useService;
